import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import {
  DescriptionCaption,
  DescriptionCol,
  DescriptionRow,
} from '@/components/Description';
import { MAX_CUSTOM_TRANSACTION_ITEMS_COUNT } from '@/features/transaction_items/List/Table/TableAction/TableAction';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { useTranslation } from '@/i18n';
import { FormCheckbox, Label } from '@moneyforward/ap-frontend-components';
import { FC, memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

export type Props = {
  mode: TransactionItemDrawerMode;
  visibilityLimitApplies?: boolean;
  visibleCustomItemsInUseCount?: number;
  showVisibleLimitError?: boolean;
  initialIsVisible?: boolean;
};

export const VisibilityCheckbox: FC<Props> = memo(
  ({
    mode,
    visibilityLimitApplies = false,
    visibleCustomItemsInUseCount = 0,
    showVisibleLimitError = false,
    initialIsVisible: initialIsVisibleProp,
  }) => {
    const { t } = useTranslation();
    const { control, getValues } = useFormContext<TransactionItemFormType>();
    const isVisible = useWatch({ control, name: 'is_visible' });
    const initialIsVisible =
      initialIsVisibleProp ?? Boolean(getValues('is_visible'));

    const isAtVisibleCustomLimit = useMemo(
      () => visibleCustomItemsInUseCount >= MAX_CUSTOM_TRANSACTION_ITEMS_COUNT,
      [visibleCustomItemsInUseCount]
    );

    const isUseCheckboxDisabled = useMemo(() => {
      if (mode === TransactionItemDrawerModeEnum.DETAIL) return false;
      return (
        visibilityLimitApplies &&
        isAtVisibleCustomLimit &&
        !isVisible &&
        !initialIsVisible
      );
    }, [
      initialIsVisible,
      isAtVisibleCustomLimit,
      isVisible,
      mode,
      visibilityLimitApplies,
    ]);

    const showVisibleLimitWarning =
      !showVisibleLimitError && isUseCheckboxDisabled;

    return (
      <DescriptionRow>
        <DescriptionCol
          label={
            <Label testId='visibility'>
              <BoldLabelText>
                {t('transaction_items.table.is_visible')}
              </BoldLabelText>
            </Label>
          }
          labelWidth='176px'
        />
        <DescriptionCol
          value={
            <>
              {mode === TransactionItemDrawerModeEnum.DETAIL ? (
                <div data-testid='visibility-detail'>
                  {isVisible
                    ? t('transaction_items.table.visible')
                    : t('transaction_items.table.invisible')}
                </div>
              ) : (
                <div>
                  <FormCheckbox<TransactionItemFormType>
                    name='is_visible'
                    control={control}
                    inputId='is_visible'
                    testId='visibility'
                    label={t('transaction_items.table.visible')}
                    disabled={isUseCheckboxDisabled}
                  />
                  <DescriptionCaption>
                    {t('transaction_items.visibility.caption')}
                  </DescriptionCaption>
                  {showVisibleLimitWarning && (
                    <DescriptionCaption type='alert'>
                      {t('transaction_items.visibility.active_limit_warning')}
                    </DescriptionCaption>
                  )}
                  {showVisibleLimitError && (
                    <DescriptionCaption type='error'>
                      {t('transaction_items.visibility.active_limit_warning')}
                    </DescriptionCaption>
                  )}
                </div>
              )}
            </>
          }
        />
      </DescriptionRow>
    );
  }
);
VisibilityCheckbox.displayName = 'VisibilityCheckbox';
